import { useEffect, useRef } from 'react'
import Header from "@/components/Header"
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion"

const faqs = [
  {
    question: "Como funciona o Simples.ECG?",
    answer: "O Simples.ECG é uma plataforma gratuita que utiliza inteligência artificial avançada para auxiliar na interpretação de eletrocardiogramas. Basta fazer o upload da imagem e nosso sistema fornecerá uma análise detalhada em segundos, destacando pontos relevantes e sugerindo possíveis achados. A ferramenta foi desenvolvida para dar mais segurança e agilidade aos médicos plantonistas."
  },
  {
    question: "O Simples.ECG é realmente gratuito?",
    answer: "Sim! O Simples.ECG é uma ferramenta totalmente gratuita oferecida pela Simples.Health para ajudar profissionais de saúde. Não há limites de uso nem custos ocultos. Esta é nossa contribuição para melhorar o dia a dia dos profissionais de saúde."
  },
  {
    question: "Qual é o tempo médio de resposta para cada análise?",
    answer: "Nossa IA processa as imagens em segundos, geralmente fornecendo uma análise completa em menos de 15 segundos. Isso permite que você mantenha um fluxo de trabalho eficiente durante plantões movimentados, sem comprometer a qualidade da avaliação."
  },
  {
    question: "Quais tipos de imagens são suportados?",
    answer: "Atualmente, o sistema suporta imagens de eletrocardiogramas (ECG) nos formatos mais comuns, incluindo:\n- ECG de 12 derivações\n- ECG de repouso\n- ECG de esforço\n- Holter e monitorização contínua\n- Tiras de ritmo\n- ECG obtidos por dispositivos móveis"
  },
  {
    question: "Como é garantida a precisão das análises?",
    answer: "Nossa IA foi treinada com milhares de eletrocardiogramas e laudos validados por especialistas. O sistema é constantemente atualizado e passa por testes de qualidade. No entanto, é importante ressaltar que a ferramenta é um auxílio ao diagnóstico e não substitui o julgamento clínico do médico."
  },
  {
    question: "Qual a relação com a Simples.Health?",
    answer: "O Simples.ECG é um produto gratuito da Simples.Health, uma plataforma que reduz em 80% o tempo gasto com documentação médica. Enquanto o Simples.ECG é uma ferramenta focada em análise de eletrocardiogramas, a Simples.Health automatiza toda a documentação da consulta em tempo real, gerando prontuários, receitas, laudos, atestados e outros documentos com apenas um clique."
  },
  {
    question: "Posso usar em diferentes dispositivos?",
    answer: "Sim! O Simples.ECG é uma plataforma web responsiva que funciona em qualquer dispositivo com acesso à internet. Você pode alternar entre desktop, tablet e smartphone mantendo acesso a todas as funcionalidades."
  },
  {
    question: "Como é garantida a segurança dos dados?",
    answer: "Implementamos as mais rigorosas medidas de segurança:\n- Criptografia de ponta a ponta\n- Pré-processamento de imagens para garantir a anonimização\n- Conformidade com LGPD e regulamentações de saúde\n- Backups regulares e seguros\n- Acesso restrito e monitorado\nTodas as imagens são processadas seguindo protocolos estritos de privacidade."
  },
  {
    question: "O sistema funciona offline?",
    answer: "No momento, o Simples.ECG requer conexão com internet para realizar as análises. No entanto, estamos desenvolvendo uma versão com funcionalidades offline limitadas para situações de conectividade instável durante plantões."
  },
  {
    question: "Como posso conhecer a Simples.Health?",
    answer: "A Simples.Health é nossa plataforma que utiliza IA para automatizar a documentação médica, reduzindo em 80% o tempo gasto com burocracia. Visite simples.health para conhecer todos os recursos disponíveis:\n- Documentação completa por IA durante a consulta\n- Receitas, Laudos, Atestados, Encaminhamentos e Pedidos de Exame com um clique\n- Registro automático de resultados de exames, laudos e documentos clínicos\n- Resumo Inteligente para começar a consulta mais rápido\n\nTenha mais tempo livre para seus pacientes, para realizar mais consultas ou para sua família."
  },
  {
    question: "Como o sistema lida com diferentes padrões de ECG?",
    answer: "Nossa IA é treinada para reconhecer e se adaptar a diferentes padrões de eletrocardiogramas, como ECG de 12 derivações, tiras de ritmo e registros de diferentes equipamentos. O sistema considera fatores como calibração, velocidade do papel e formato para fornecer análises precisas independentemente da origem do ECG."
  }
]

export default function FAQ() {
  const canvasRef = useRef<HTMLCanvasElement>(null)

  useEffect(() => {
    const canvas = canvasRef.current
    if (!canvas) return

    const ctx = canvas.getContext('2d')
    if (!ctx) return

    const resizeCanvas = () => {
      canvas.width = window.innerWidth
      canvas.height = window.innerHeight
    }
    resizeCanvas()
    window.addEventListener('resize', resizeCanvas)

    // Reduzido o número de partículas
    const particles: Array<{x: number, y: number, size: number, speedX: number, speedY: number, opacity: number}> = []
    const particleCount = 25

    for (let i = 0; i < particleCount; i++) {
      particles.push({
        x: Math.random() * canvas.width,
        y: Math.random() * canvas.height,
        size: Math.random() * 3 + 1,
        speedX: Math.random() * 1.5 - 0.75,
        speedY: Math.random() * 1.5 - 0.75,
        opacity: Math.random() * 0.3 + 0.1
      })
    }

    const drawXrayGrid = () => {
      ctx.strokeStyle = 'rgba(44, 62, 80, 0.1)'
      ctx.lineWidth = 0.5
      
      for (let x = 0; x < canvas.width; x += 50) {
        ctx.beginPath()
        ctx.moveTo(x, 0)
        ctx.lineTo(x, canvas.height)
        ctx.stroke()
      }
      
      for (let y = 0; y < canvas.height; y += 50) {
        ctx.beginPath()
        ctx.moveTo(0, y)
        ctx.lineTo(canvas.width, y)
        ctx.stroke()
      }

      ctx.strokeStyle = 'rgba(44, 62, 80, 0.05)'
      ctx.lineWidth = 0.2
      
      for (let x = 0; x < canvas.width; x += 10) {
        ctx.beginPath()
        ctx.moveTo(x, 0)
        ctx.lineTo(x, canvas.height)
        ctx.stroke()
      }
      
      for (let y = 0; y < canvas.height; y += 10) {
        ctx.beginPath()
        ctx.moveTo(0, y)
        ctx.lineTo(canvas.width, y)
        ctx.stroke()
      }
    }

    let scanLinePos = 0
    const drawScanLine = () => {
      ctx.fillStyle = 'rgba(44, 62, 80, 0.1)'
      ctx.fillRect(0, scanLinePos, canvas.width, 2)
      scanLinePos = (scanLinePos + 2) % canvas.height
    }

    const drawMarkers = () => {
      ctx.fillStyle = 'rgba(44, 62, 80, 0.3)'
      ctx.font = '10px monospace'
      
      for (let y = 0; y < canvas.height; y += 50) {
        ctx.fillText(`${y}`, 5, y + 10)
      }
      
      for (let x = 0; x < canvas.width; x += 50) {
        ctx.fillText(`${x}`, x, 10)
      }
    }

    const animate = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height)
      drawXrayGrid()
      drawScanLine()
      drawMarkers()

      particles.forEach((particle, i) => {
        particle.x += particle.speedX
        particle.y += particle.speedY

        if (particle.x < 0) particle.x = canvas.width
        if (particle.x > canvas.width) particle.x = 0
        if (particle.y < 0) particle.y = canvas.height
        if (particle.y > canvas.height) particle.y = 0

        ctx.beginPath()
        ctx.fillStyle = `rgba(44, 62, 80, ${particle.opacity})`
        ctx.arc(particle.x, particle.y, particle.size, 0, Math.PI * 2)
        ctx.fill()

        for (let j = i + 1; j < particles.length; j++) {
          const p2 = particles[j]
          const distance = Math.sqrt(Math.pow(particle.x - p2.x, 2) + Math.pow(particle.y - p2.y, 2))
          
          if (distance < 100) {
            ctx.beginPath()
            ctx.strokeStyle = `rgba(44, 62, 80, ${0.1 * (1 - distance / 100)})`
            ctx.lineWidth = 0.5
            ctx.moveTo(particle.x, particle.y)
            ctx.lineTo(p2.x, p2.y)
            ctx.stroke()
          }
        }
      })

      requestAnimationFrame(animate)
    }

    animate()

    return () => window.removeEventListener('resize', resizeCanvas)
  }, [])

  return (
    <div className="relative min-h-screen overflow-hidden">
      {/* Gradientes animados em camadas */}
      <div className="absolute inset-0">
        <div className="absolute inset-0 bg-gradient-to-b from-background via-primary/5 to-background" />
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,_var(--tw-gradient-stops))] from-primary/10 via-transparent to-transparent animate-pulse-slow" />
        <div className="absolute inset-0 bg-gradient-to-br from-primary/5 via-transparent to-primary/10 animate-gradient-slow" />
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,_transparent_50%,_rgba(0,0,0,0.1)_100%)]" />
      </div>
      
      <canvas
        ref={canvasRef}
        className="absolute inset-0 pointer-events-none"
        style={{ opacity: 0.7 }}
      />

      <div className="relative">
        <Header showBackButton />

        <div className="container mx-auto px-4 py-16">
          <div className="text-center mb-16 pt-16">
            <h1 className="text-4xl font-bold mb-4">Dúvidas Frequentes</h1>
            <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
              Encontre respostas para as perguntas mais comuns sobre o Simples.ECG
            </p>
          </div>

          <div className="max-w-3xl mx-auto">
            <Accordion type="single" collapsible className="w-full">
              {faqs.map((faq, index) => (
                <AccordionItem key={index} value={`item-${index}`}>
                  <AccordionTrigger className="text-left text-lg font-medium">
                    {faq.question}
                  </AccordionTrigger>
                  <AccordionContent className="text-muted-foreground whitespace-pre-line">
                    {faq.answer}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>

          {/* Footer */}
          <div className="mt-16 text-center">
            <a href="https://simples.health" target="_blank" rel="noreferrer" className="inline-block mb-4">
              <img 
                src="/Logo Escrito.png" 
                alt="Simples.Health" 
                className="h-8 w-auto hover:opacity-80 transition-opacity mx-auto"
              />
            </a>
            <p className="text-sm text-muted-foreground">
              © {new Date().getFullYear()} Simples.Health. Todos os direitos reservados.
            </p>
          </div>
        </div>
      </div>
    </div>
  )
} 